'use client';

import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard,
  ClipboardCheck,
  Zap,
  AlertTriangle,
  MapPin,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Camera,
  Send,
  ArrowLeft,
  Wifi,
  WifiOff,
  Package,
  ClipboardList,
  Activity,
  Users,
  ChevronDown,
  Plus,
  FileText,
  Wrench,
  Eye,
  Radio,
} from 'lucide-react';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';


// ─── Types ───────────────────────────────────────────────────────────────────

type Tab = 'dashboard' | 'inspect' | 'forms' | 'alerts';

interface InspectionForm {
  id: string;
  title: string;
  category: 'transformer' | 'line' | 'meter' | 'substation' | 'pole';
  fields: FormField[];
}

interface FormField {
  id: string;
  label: string;
  type: 'text' | 'select' | 'radio' | 'number' | 'textarea' | 'checkbox';
  options?: string[];
  required?: boolean;
}

interface FieldAlert {
  id: string;
  type: 'fault' | 'maintenance' | 'inspection';
  title: string;
  location: string;
  severity: 'critical' | 'high' | 'medium';
  time: string;
}

interface SubmittedReport {
  id: string;
  formTitle: string;
  location: string;
  submittedAt: string;
  status: 'submitted' | 'reviewed' | 'pending';
}

// ─── Static Data ─────────────────────────────────────────────────────────────

const inspectionForms: InspectionForm[] = [
  {
    id: 'tf-001',
    title: 'Transformer Inspection',
    category: 'transformer',
    fields: [
      { id: 'location', label: 'Site / Location', type: 'text', required: true },
      { id: 'tf_id', label: 'Transformer ID', type: 'text', required: true },
      { id: 'capacity', label: 'Rated Capacity (kVA)', type: 'number', required: true },
      {
        id: 'condition',
        label: 'Overall Condition',
        type: 'radio',
        options: ['Good', 'Fair', 'Poor', 'Critical'],
        required: true,
      },
      {
        id: 'oil_level',
        label: 'Oil Level',
        type: 'select',
        options: ['Normal', 'Low', 'Very Low', 'Leaking'],
        required: true,
      },
      { id: 'rust', label: 'Rust / Corrosion Present', type: 'checkbox' },
      { id: 'bushing', label: 'Bushing Damage', type: 'checkbox' },
      { id: 'remarks', label: 'Inspector Remarks', type: 'textarea' },
    ],
  },
  {
    id: 'ln-001',
    title: 'Distribution Line Check',
    category: 'line',
    fields: [
      { id: 'location', label: 'Line Section / Route', type: 'text', required: true },
      { id: 'voltage', label: 'Voltage Level (kV)', type: 'number', required: true },
      {
        id: 'conductor',
        label: 'Conductor Condition',
        type: 'radio',
        options: ['Intact', 'Sagging', 'Broken', 'Corroded'],
        required: true,
      },
      { id: 'clearance', label: 'Adequate Ground Clearance', type: 'checkbox' },
      { id: 'vegetation', label: 'Vegetation Encroachment', type: 'checkbox' },
      { id: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
  },
  {
    id: 'mt-001',
    title: 'Meter Reading & Audit',
    category: 'meter',
    fields: [
      { id: 'account', label: 'Account Number', type: 'text', required: true },
      { id: 'meter_id', label: 'Meter Serial No.', type: 'text', required: true },
      { id: 'reading', label: 'Current Reading (kWh)', type: 'number', required: true },
      {
        id: 'meter_type',
        label: 'Meter Type',
        type: 'select',
        options: ['Prepaid', 'Postpaid', 'Smart'],
        required: true,
      },
      { id: 'tamper', label: 'Tamper Evidence', type: 'checkbox' },
      { id: 'bypass', label: 'Bypass Detected', type: 'checkbox' },
      { id: 'remarks', label: 'Field Notes', type: 'textarea' },
    ],
  },
  {
    id: 'ss-001',
    title: 'Substation Walkthrough',
    category: 'substation',
    fields: [
      { id: 'ss_name', label: 'Substation Name', type: 'text', required: true },
      {
        id: 'status',
        label: 'Operational Status',
        type: 'radio',
        options: ['Fully Operational', 'Partial', 'Offline'],
        required: true,
      },
      { id: 'breakers', label: 'All Breakers Functional', type: 'checkbox' },
      { id: 'earthing', label: 'Earthing Intact', type: 'checkbox' },
      { id: 'fire_ext', label: 'Fire Extinguisher Present', type: 'checkbox' },
      { id: 'remarks', label: 'Observations', type: 'textarea' },
    ],
  },
  {
    id: 'pl-001',
    title: 'Pole & Structure Audit',
    category: 'pole',
    fields: [
      { id: 'pole_id', label: 'Pole ID / GPS Ref', type: 'text', required: true },
      {
        id: 'material',
        label: 'Pole Material',
        type: 'select',
        options: ['Concrete', 'Wood', 'Steel'],
        required: true,
      },
      {
        id: 'condition',
        label: 'Structural Condition',
        type: 'radio',
        options: ['Sound', 'Cracked', 'Leaning', 'Broken'],
        required: true,
      },
      { id: 'stay_wire', label: 'Stay Wire Intact', type: 'checkbox' },
      { id: 'numbering', label: 'Pole Numbering Visible', type: 'checkbox' },
      { id: 'remarks', label: 'Notes', type: 'textarea' },
    ],
  },
];

const fieldAlerts: FieldAlert[] = [
  {
    id: 'a1',
    type: 'fault',
    title: 'Transformer Fault — Kumasi North',
    location: 'Kumasi North District',
    severity: 'critical',
    time: '2h ago',
  },
  {
    id: 'a2',
    type: 'maintenance',
    title: 'Scheduled Line Maintenance',
    location: 'Accra East Feeder 3',
    severity: 'medium',
    time: '4h ago',
  },
  {
    id: 'a3',
    type: 'inspection',
    title: 'Overdue Meter Audit',
    location: 'Takoradi Industrial Zone',
    severity: 'high',
    time: '1d ago',
  },
  {
    id: 'a4',
    type: 'fault',
    title: 'Conductor Sagging Reported',
    location: 'Tamale South Route 7',
    severity: 'high',
    time: '6h ago',
  },
];

const submittedReports: SubmittedReport[] = [
  {
    id: 'r1',
    formTitle: 'Transformer Inspection',
    location: 'Accra Central',
    submittedAt: 'Today, 09:14',
    status: 'reviewed',
  },
  {
    id: 'r2',
    formTitle: 'Meter Reading & Audit',
    location: 'Tema Port Zone',
    submittedAt: 'Today, 07:30',
    status: 'submitted',
  },
  {
    id: 'r3',
    formTitle: 'Pole & Structure Audit',
    location: 'Kumasi Ashanti',
    submittedAt: 'Yesterday, 15:45',
    status: 'pending',
  },
];

const categoryIcon: Record<InspectionForm['category'], React.ElementType> = {
  transformer: Zap,
  line: Radio,
  meter: Activity,
  substation: Wrench,
  pole: MapPin,
};

const categoryColor: Record<InspectionForm['category'], string> = {
  transformer: '#F59E0B',
  line: '#3B82F6',
  meter: '#10B981',
  substation: '#8B5CF6',
  pole: '#EF4444',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function DashboardTab() {
  const { kpis, profile } = useCountry();
  const { fmtNum } = useLanguage();

  const kpiIconMap: Record<string, React.ElementType> = {
    Package,
    ClipboardList,
    AlertTriangle,
    Zap,
    Activity,
    Users,
  };

  return (
    <div className="space-y-5 pb-6">
      {/* Welcome strip */}
      <div
        className="rounded-2xl p-4"
        style={{
          background: 'linear-gradient(135deg, rgba(13,59,46,0.9) 0%, rgba(5,30,60,0.9) 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--accent)' }}>
          Field Inspector Portal
        </p>
        <h2 className="text-white font-bold text-lg leading-tight">{profile?.utility}</h2>
        <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.55)' }}>
          {profile?.program} · {profile?.name}
        </p>
      </div>

      {/* KPI Grid */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Live KPIs
        </p>
        <div className="grid grid-cols-2 gap-3">
          {kpis?.map((kpi) => {
            const IconComp = kpiIconMap[kpi.iconName] || Activity;
            const rawNum = typeof kpi.value === 'string' ? parseFloat(kpi.value.replace(/[^0-9.]/g, '')) : kpi.value;
            const displayVal = !isNaN(rawNum) ? fmtNum(rawNum) : kpi.value;
            return (
              <div
                key={kpi.id}
                className="rounded-xl p-3.5 relative overflow-hidden"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.09)',
                }}
              >
                {kpi.alert && (
                  <span
                    className="absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: 'var(--status-red)', color: 'white' }}
                  >
                    !
                  </span>
                )}
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center mb-2"
                  style={{ backgroundColor: `${kpi.iconColor}22` }}
                >
                  <IconComp size={16} style={{ color: kpi.iconColor }} strokeWidth={1.8} />
                </div>
                <p className="text-xl font-bold text-white tabular-nums">{displayVal}</p>
                <p className="text-[10px] mt-0.5 leading-tight" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  {kpi.label}
                </p>
                <p
                  className={`text-[10px] mt-1 font-medium ${kpi.deltaPositive ? 'text-green-400' : 'text-red-400'}`}
                >
                  {kpi.deltaPositive ? '↑' : '↓'} {kpi.delta}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Reports */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Recent Submissions
        </p>
        <div className="space-y-2">
          {submittedReports.map((r) => (
            <div
              key={r.id}
              className="flex items-center gap-3 rounded-xl px-3.5 py-3"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor:
                    r.status === 'reviewed' ?'rgba(16,185,129,0.15)'
                      : r.status === 'submitted' ?'rgba(59,130,246,0.15)' :'rgba(245,158,11,0.15)',
                }}
              >
                {r.status === 'reviewed' ? (
                  <CheckCircle2 size={15} className="text-green-400" />
                ) : r.status === 'submitted' ? (
                  <Clock size={15} className="text-blue-400" />
                ) : (
                  <Clock size={15} className="text-amber-400" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{r.formTitle}</p>
                <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {r.location} · {r.submittedAt}
                </p>
              </div>
              <span
                className="text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 capitalize"
                style={{
                  backgroundColor:
                    r.status === 'reviewed' ?'rgba(16,185,129,0.18)'
                      : r.status === 'submitted' ?'rgba(59,130,246,0.18)' :'rgba(245,158,11,0.18)',
                  color:
                    r.status === 'reviewed' ? '#34D399' : r.status === 'submitted' ? '#60A5FA' : '#FCD34D',
                }}
              >
                {r.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
          Quick Actions
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { label: 'New Inspection', icon: ClipboardCheck, color: '#F59E0B' },
            { label: 'Report Fault', icon: AlertTriangle, color: '#EF4444' },
            { label: 'View Map', icon: MapPin, color: '#3B82F6' },
            { label: 'Upload Photo', icon: Camera, color: '#8B5CF6' },
          ].map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.label}
                className="flex items-center gap-2.5 rounded-xl px-3.5 py-3 text-left transition-all active:scale-95"
                style={{
                  background: `${action.color}12`,
                  border: `1px solid ${action.color}30`,
                }}
              >
                <Icon size={18} style={{ color: action.color }} strokeWidth={1.8} />
                <span className="text-sm font-medium text-white">{action.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function FormsTab({ onStartForm }: { onStartForm: (form: InspectionForm) => void }) {
  return (
    <div className="space-y-4 pb-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--muted-foreground)' }}>
          Monitoring Forms
        </p>
        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Select a form to begin field inspection
        </p>
      </div>
      <div className="space-y-2.5">
        {inspectionForms.map((form) => {
          const Icon = categoryIcon[form.category];
          const color = categoryColor[form.category];
          return (
            <button
              key={form.id}
              onClick={() => onStartForm(form)}
              className="w-full flex items-center gap-3.5 rounded-xl px-4 py-3.5 text-left transition-all active:scale-[0.98]"
              style={{
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.09)',
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${color}20` }}
              >
                <Icon size={20} style={{ color }} strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-white">{form.title}</p>
                <p className="text-[11px] mt-0.5 capitalize" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  {form.fields.length} fields · {form.category}
                </p>
              </div>
              <ChevronRight size={16} style={{ color: 'rgba(255,255,255,0.3)' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AlertsTab() {
  const severityConfig = {
    critical: { bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', text: '#F87171', dot: '#EF4444' },
    high: { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', text: '#FCD34D', dot: '#F59E0B' },
    medium: { bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', text: '#93C5FD', dot: '#3B82F6' },
  };

  const typeIcon: Record<FieldAlert['type'], React.ElementType> = {
    fault: XCircle,
    maintenance: Wrench,
    inspection: Eye,
  };

  return (
    <div className="space-y-4 pb-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--muted-foreground)' }}>
            Field Alerts
          </p>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {fieldAlerts.length} active alerts
          </p>
        </div>
        <span
          className="text-[10px] font-bold px-2 py-1 rounded-full"
          style={{ backgroundColor: 'rgba(239,68,68,0.2)', color: '#F87171' }}
        >
          {fieldAlerts.filter((a) => a.severity === 'critical').length} Critical
        </span>
      </div>
      <div className="space-y-2.5">
        {fieldAlerts.map((alert) => {
          const cfg = severityConfig[alert.severity];
          const Icon = typeIcon[alert.type];
          return (
            <div
              key={alert.id}
              className="rounded-xl px-4 py-3.5"
              style={{ background: cfg.bg, border: `1px solid ${cfg.border}` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: `${cfg.dot}20` }}
                >
                  <Icon size={15} style={{ color: cfg.dot }} strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-white leading-tight">{alert.title}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin size={10} style={{ color: 'rgba(255,255,255,0.4)' }} />
                    <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      {alert.location}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  <span
                    className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase"
                    style={{ backgroundColor: `${cfg.dot}25`, color: cfg.text }}
                  >
                    {alert.severity}
                  </span>
                  <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.35)' }}>
                    {alert.time}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InspectionFormView({
  form,
  onBack,
}: {
  form: InspectionForm;
  onBack: () => void;
}) {
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (fieldId: string, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const color = categoryColor[form.category];
  const Icon = categoryIcon[form.category];

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}
        >
          <CheckCircle2 size={32} className="text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Report Submitted</h3>
        <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.5)' }}>
          Your {form.title} report has been recorded and sent for review.
        </p>
        <button
          onClick={onBack}
          className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all active:scale-95"
          style={{ backgroundColor: color }}
        >
          Back to Forms
        </button>
      </div>
    );
  }

  return (
    <div className="pb-8">
      {/* Form header */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={onBack}
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all active:scale-90"
          style={{ background: 'rgba(255,255,255,0.08)' }}
        >
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon size={18} style={{ color }} strokeWidth={1.8} />
        </div>
        <div>
          <h3 className="text-base font-bold text-white leading-tight">{form.title}</h3>
          <p className="text-[11px] capitalize" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {form.category} · {form.fields.length} fields
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {form.fields.map((field) => (
          <div key={field.id}>
            <label className="block text-xs font-semibold mb-1.5 text-white">
              {field.label}
              {field.required && <span className="text-red-400 ml-0.5">*</span>}
            </label>

            {field.type === 'text' && (
              <input
                type="text"
                value={(values[field.id] as string) || ''}
                onChange={(e) => handleChange(field.id, e.target.value)}
                required={field.required}
                className="w-full rounded-xl px-3.5 py-2.5 text-sm text-white outline-none focus:ring-2 transition-all"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  // @ts-ignore
                  '--tw-ring-color': color,
                }}
                placeholder={`Enter ${field.label.toLowerCase()}`}
              />
            )}

            {field.type === 'number' && (
              <input
                type="number"
                value={(values[field.id] as string) || ''}
                onChange={(e) => handleChange(field.id, e.target.value)}
                required={field.required}
                className="w-full rounded-xl px-3.5 py-2.5 text-sm text-white outline-none focus:ring-2 transition-all"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
                placeholder="0"
              />
            )}

            {field.type === 'textarea' && (
              <textarea
                rows={3}
                value={(values[field.id] as string) || ''}
                onChange={(e) => handleChange(field.id, e.target.value)}
                className="w-full rounded-xl px-3.5 py-2.5 text-sm text-white outline-none resize-none transition-all"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
                placeholder="Add notes..."
              />
            )}

            {field.type === 'select' && field.options && (
              <div className="relative">
                <select
                  value={(values[field.id] as string) || ''}
                  onChange={(e) => handleChange(field.id, e.target.value)}
                  required={field.required}
                  className="w-full rounded-xl px-3.5 py-2.5 text-sm text-white outline-none appearance-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                >
                  <option value="" style={{ background: '#0f172a' }}>
                    Select…
                  </option>
                  {field.options.map((opt) => (
                    <option key={opt} value={opt} style={{ background: '#0f172a' }}>
                      {opt}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  style={{ color: 'rgba(255,255,255,0.4)' }}
                />
              </div>
            )}

            {field.type === 'radio' && field.options && (
              <div className="grid grid-cols-2 gap-2">
                {field.options.map((opt) => {
                  const isSelected = values[field.id] === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => handleChange(field.id, opt)}
                      className="rounded-xl px-3 py-2 text-xs font-semibold text-center transition-all active:scale-95"
                      style={{
                        background: isSelected ? `${color}25` : 'rgba(255,255,255,0.05)',
                        border: isSelected ? `1.5px solid ${color}` : '1px solid rgba(255,255,255,0.1)',
                        color: isSelected ? color : 'rgba(255,255,255,0.6)',
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}

            {field.type === 'checkbox' && (
              <button
                type="button"
                onClick={() => handleChange(field.id, !values[field.id])}
                className="flex items-center gap-2.5 w-full rounded-xl px-3.5 py-2.5 text-left transition-all active:scale-[0.98]"
                style={{
                  background: values[field.id] ? `${color}15` : 'rgba(255,255,255,0.05)',
                  border: values[field.id]
                    ? `1.5px solid ${color}50`
                    : '1px solid rgba(255,255,255,0.1)',
                }}
              >
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-all"
                  style={{
                    background: values[field.id] ? color : 'rgba(255,255,255,0.08)',
                    border: values[field.id] ? 'none' : '1px solid rgba(255,255,255,0.2)',
                  }}
                >
                  {values[field.id] && <CheckCircle2 size={12} className="text-white" />}
                </div>
                <span className="text-sm text-white">{field.label}</span>
              </button>
            )}
          </div>
        ))}

        {/* Photo capture placeholder */}
        <div>
          <label className="block text-xs font-semibold mb-1.5 text-white">
            Attach Photo (optional)
          </label>
          <button
            type="button"
            className="w-full rounded-xl py-4 flex flex-col items-center gap-2 transition-all active:scale-[0.98]"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1.5px dashed rgba(255,255,255,0.15)',
            }}
          >
            <Camera size={22} style={{ color: 'rgba(255,255,255,0.35)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Tap to capture or upload
            </span>
          </button>
        </div>

        <button
          type="submit"
          className="w-full flex items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white transition-all active:scale-[0.97] mt-2"
          style={{ backgroundColor: color }}
        >
          <Send size={16} />
          Submit Report
        </button>
      </form>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function MobileFieldPage() {
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [activeForm, setActiveForm] = useState<InspectionForm | null>(null);
  const [isOnline, setIsOnline] = useState(true);

  useEffect(() => {
    const update = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    update();
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'forms', label: 'Forms', icon: ClipboardCheck },
    { id: 'alerts', label: 'Alerts', icon: AlertTriangle },
  ];

  const handleStartForm = (form: InspectionForm) => {
    setActiveForm(form);
    setActiveTab('inspect' as Tab);
  };

  const handleBackFromForm = () => {
    setActiveForm(null);
    setActiveTab('forms');
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: 'var(--primary-dark)', fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Mobile frame wrapper — centered on desktop */}
      <div className="w-full max-w-sm mx-auto flex flex-col min-h-screen relative">
        {/* Status bar */}
        <div
          className="flex items-center justify-between px-4 pt-3 pb-2 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <div className="flex items-center gap-2">
            <AppLogo size={22} />
            <span className="text-white font-bold text-sm">
              WestTrack<span style={{ color: 'var(--accent)' }}>GIS</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isOnline ? (
              <div className="flex items-center gap-1">
                <Wifi size={13} className="text-green-400" />
                <span className="text-[10px] text-green-400 font-medium">Online</span>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <WifiOff size={13} className="text-red-400" />
                <span className="text-[10px] text-red-400 font-medium">Offline</span>
              </div>
            )}
            <a
              href="/"
              className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-lg transition-all"
              style={{
                background: 'rgba(255,255,255,0.07)',
                color: 'rgba(255,255,255,0.6)',
              }}
            >
              <FileText size={11} />
              Desktop
            </a>
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-4 pt-4" style={{ paddingBottom: '80px' }}>
          {activeTab === 'dashboard' && <DashboardTab />}
          {activeTab === 'forms' && !activeForm && (
            <FormsTab onStartForm={handleStartForm} />
          )}
          {(activeTab === 'inspect' || activeForm) && activeForm && (
            <InspectionFormView form={activeForm} onBack={handleBackFromForm} />
          )}
          {activeTab === 'alerts' && <AlertsTab />}
        </div>

        {/* Bottom nav bar */}
        <div
          className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-sm flex items-center justify-around px-2 py-2 z-50"
          style={{
            background: 'rgba(5,15,30,0.97)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            backdropFilter: 'blur(16px)',
          }}
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id || (tab.id === 'forms' && activeTab === 'inspect');
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id !== 'inspect') {
                    setActiveForm(null);
                    setActiveTab(tab.id);
                  }
                }}
                className="flex flex-col items-center gap-1 px-5 py-1.5 rounded-xl transition-all active:scale-90 relative"
                style={{
                  background: isActive ? 'rgba(255,255,255,0.08)' : 'transparent',
                }}
              >
                {tab.id === 'alerts' && (
                  <span
                    className="absolute top-1 right-3 w-2 h-2 rounded-full"
                    style={{ backgroundColor: 'var(--status-red)' }}
                  />
                )}
                <Icon
                  size={20}
                  strokeWidth={isActive ? 2.2 : 1.6}
                  style={{ color: isActive ? 'var(--accent)' : 'rgba(255,255,255,0.4)' }}
                />
                <span
                  className="text-[10px] font-semibold"
                  style={{ color: isActive ? 'var(--accent)' : 'rgba(255,255,255,0.4)' }}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}

          {/* FAB for new form */}
          <button
            onClick={() => {
              setActiveForm(null);
              setActiveTab('forms');
            }}
            className="flex flex-col items-center gap-1 px-5 py-1.5 rounded-xl transition-all active:scale-90"
            style={{ background: 'transparent' }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'var(--accent)' }}
            >
              <Plus size={18} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="text-[10px] font-semibold" style={{ color: 'rgba(255,255,255,0.4)' }}>
              New
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
